import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Funnel, TrendingDown, AlertCircle, Loader2 } from 'lucide-react';
import { cn } from '../lib/utils';
import { toast } from 'sonner';

export default function ConversionFunnel() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get('/admin/analytics/funnel?days=30')
            .then((r) => setData(r.data))
            .catch((e) => toast.error('No se pudo cargar el funnel: ' + (e?.response?.data?.detail || e.message)))
            .finally(() => setLoading(false));
    }, []);

    if (loading) {
        return <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
    }
    if (!data) return null;

    const maxN = Math.max(...data.stages.map((s) => s.n), 1);

    return (
        <Card data-testid="conversion-funnel">
            <CardHeader>
                <CardTitle className="flex items-center gap-2 font-heading text-base">
                    <Funnel className="h-4 w-4 text-slate-700" />
                    Embudo de conversión · últimos {data.ventana_dias} días
                </CardTitle>
                <p className="text-xs text-slate-500">
                    Identifica exactamente dónde se pierden los docentes en su recorrido.
                </p>
            </CardHeader>
            <CardContent className="space-y-3">
                {data.stages.map((s, i) => {
                    const width = (s.n / maxN) * 100;
                    const isFirst = i === 0;
                    const lost = i > 0 ? Math.max(0, data.stages[i - 1].n - s.n) : 0;
                    return (
                        <div key={s.id} className="space-y-1">
                            <div className="flex items-center justify-between text-xs">
                                <span className="font-medium text-slate-800">{i + 1}. {s.label}</span>
                                <div className="flex items-center gap-2">
                                    <span className="font-mono text-slate-900 font-semibold">{s.n.toLocaleString('es-CL')}</span>
                                    {!isFirst && (
                                        <Badge variant="outline" className="text-[10px]">
                                            {s.conv_vs_inicio}% del total
                                        </Badge>
                                    )}
                                </div>
                            </div>
                            <div className="h-7 rounded-md bg-slate-100 overflow-hidden">
                                <div
                                    className={cn(
                                        'h-full rounded-md flex items-center px-2 text-[10px] text-white font-semibold transition-all',
                                        isFirst
                                            ? 'bg-blue-600'
                                            : s.conv_vs_inicio >= 5
                                            ? 'bg-emerald-600'
                                            : s.conv_vs_inicio >= 1
                                            ? 'bg-amber-600'
                                            : 'bg-rose-600'
                                    )}
                                    style={{ width: `${Math.max(2, width)}%` }}
                                >
                                    {!isFirst && lost > 0 && (
                                        <span className="ml-auto opacity-90">−{lost.toLocaleString('es-CL')} perdidos</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}

                {/* Diagnóstico */}
                <div className="mt-4 grid sm:grid-cols-2 gap-3">
                    <div className="rounded-lg border border-rose-200 bg-rose-50/60 p-3">
                        <div className="flex items-center gap-2 text-xs font-semibold text-rose-900">
                            <TrendingDown className="h-3.5 w-3.5" />
                            Principal drop-off
                        </div>
                        {data.principal_drop_off && (
                            <p className="mt-1 text-xs text-rose-900 leading-relaxed">
                                <strong>{data.principal_drop_off.from}</strong> → <strong>{data.principal_drop_off.to}</strong>:
                                {' '}<span className="font-mono">{data.principal_drop_off.lost.toLocaleString('es-CL')}</span> docentes perdidos
                                <span className="text-rose-700"> ({data.principal_drop_off.drop_pct}%)</span>.
                            </p>
                        )}
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                        <div className="flex items-center gap-2 text-xs font-semibold text-slate-700">
                            <AlertCircle className="h-3.5 w-3.5" />
                            Tasa de rebote
                        </div>
                        <p className="mt-1 text-xs text-slate-700 leading-relaxed">
                            <span className="font-mono font-bold text-slate-900">{data.bounce_rate}%</span> de los visitantes únicos vieron <strong>una sola página</strong> y se fueron sin navegar a otra.
                        </p>
                    </div>
                </div>

                <div className="rounded-lg border border-blue-200 bg-blue-50/60 p-3 text-xs text-blue-900 leading-relaxed">
                    <strong>Lectura del funnel:</strong> {data.diagnostico}
                </div>

                <p className="text-[10px] text-slate-400 italic text-center pt-1">
                    Totales acumulados (sin filtro de fecha) — Registrados: <strong>{data.totales_acumulados.registros_total}</strong> ·
                    Con estrategia elegida: <strong>{data.totales_acumulados.con_estrategia_total}</strong>
                    <span className="mx-1">·</span>
                    <span className="text-slate-500">Con establecimiento vinculado (acción opcional): <strong>{data.totales_acumulados.con_establecimiento_total}</strong></span>
                </p>
            </CardContent>
        </Card>
    );
}
